import { type FunctionComponent, type ReactNode } from 'react'
import { useIntl } from 'react-intl'
import { ChatIcon } from '@instacart/ids-core'
import { notify, NotificationLevel } from '@retailer-platform/shared-components'
import {
  TDS_DEFAULT_COLORS,
  typography,
  useTheme,
} from '@retailer-platform/shared-components/src/tds'
import { useCurrentAccountContext } from '../../../utils/current-account/CurrentAccountContext'
import { FeatureToggle } from '../../../legacy/contexts/featureToggles/FeatureToggleContext'
import { useAdminControls } from '../../../utils/contexts/admin-controls/AdminControlsContext'
import useAccessControl from '../../../legacy/components/AccessControl/useAccessControl'
import { Permission } from '../../../legacy/common/types'

const FEEDBACK_SLACK_URL = 'https://instacart.enterprise.slack.com/archives/C06917XU8TF'
const FEEDBACK_TOAST_ID = 'nav-v3-feedback'

// User-facing toggle between v2 (legacy MenuBarTop) and v3 (TdsNavigationBar).
// Backed by the same `useTdsNav` admin control that the Admin Controls modal
// switch writes — persisted in localStorage. Gated on the `rt_ipp_nav_v3`
// feature toggle so users without v3 access never see either button.
//
// `target` is the version this button switches *to*: 'v3' for the opt-in
// rendered in the v2 top bar, 'v2' for the opt-out rendered in the v3 top bar.
//
// `appearance` picks the color treatment to match the surrounding nav. The
// retailer/IDP topbar is brand green, so the button uses brandPrimaryRegular.
// The admin topbar is dark gray (systemGrayscale80), where the green clashes,
// so the admin appearance uses a lighter gray that reads as a button without
// fighting the topbar's monochrome palette.
type Props = { target: 'v2' | 'v3'; appearance?: 'retailer' | 'admin' }

export const NavVersionSwitcherButton: FunctionComponent<Props> = ({
  target,
  appearance = 'retailer',
}) => {
  const intl = useIntl()
  const theme = useTheme()
  const { account } = useCurrentAccountContext()
  const { setAdminControl } = useAdminControls()
  const hasAccess = useAccessControl()
  const isInternalAdmin = hasAccess({ permissions: Permission.AdminPanel })

  const hasNavV3 = !!account?.featureToggles?.find(f => f.name === FeatureToggle.IppNavV3)
  if (!hasNavV3) return null

  const labelId =
    target === 'v3' ? 'app.nav.versionSwitcher.tryNew' : 'app.nav.versionSwitcher.switchToClassic'

  const palette =
    appearance === 'admin'
      ? {
          background: theme.colors.systemGrayscale60,
          hover: theme.colors.systemGrayscale80,
          active: theme.colors.systemGrayscale80,
        }
      : {
          background: theme.colors.brandPrimaryRegular,
          hover: theme.colors.brandPrimaryDark,
          active: theme.colors.brandPrimaryExtraDark,
        }

  return (
    <button
      type="button"
      onClick={() => {
        setAdminControl('useTdsNav', target === 'v3')
        if (isInternalAdmin) {
          notify({
            id: FEEDBACK_TOAST_ID,
            level: NotificationLevel.Info,
            timeout: 8000,
            icon: <ChatIcon fill={TDS_DEFAULT_COLORS.colors.brandPrimaryRegular} size={28} />,
            contents: intl.formatMessage(
              { id: 'app.nav.versionSwitcher.feedbackToast' },
              {
                link: (chunks: ReactNode) => (
                  <a href={FEEDBACK_SLACK_URL} target="_blank" rel="noopener noreferrer">
                    {chunks}
                  </a>
                ),
              }
            ),
          })
        }
      }}
      css={{
        ...typography.accentEmphasized,
        height: 28,
        padding: '0 16px',
        borderRadius: 4,
        border: 'none',
        cursor: 'pointer',
        color: theme.colors.systemGrayscale00,
        backgroundColor: palette.background,
        whiteSpace: 'nowrap',
        '&:hover': { backgroundColor: palette.hover },
        '&:active': { backgroundColor: palette.active },
        '&:focus-visible': {
          outline: `2px solid ${theme.colors.systemGrayscale00}`,
          outlineOffset: 2,
        },
      }}
    >
      {intl.formatMessage({ id: labelId })}
    </button>
  )
}
